import { Route } from "react-router-dom";
import { lazy, Suspense, type LazyExoticComponent, type ComponentType } from "react";
import LoadingScreen from "@/components/LoadingScreen";
import { DESTINATION_ROUTES } from "@/config/destinationRoutes";

const PortugalHub = lazy(() => import("@/pages/destinations/portugal/PortugalHub"));
const SpainHub = lazy(() => import("@/pages/destinations/spain/SpainHub"));
const ItalyHub = lazy(() => import("@/pages/destinations/italy/ItalyHub"));
const GreeceHub = lazy(() => import("@/pages/destinations/greece/GreeceHub"));

const Imobiliario = lazy(() => import("@/pages/Imobiliario"));
const ComprarCasa = lazy(() => import("@/pages/ComprarCasa"));
const Financiamento = lazy(() => import("@/pages/Financiamento"));
const ConsultoriaLegal = lazy(() => import("@/pages/ConsultoriaLegal"));
const Negocios = lazy(() => import("@/pages/Negocios"));
const Saude = lazy(() => import("@/pages/Saude"));
const GuiasEmigrantes = lazy(() => import("@/pages/GuiasEmigrantes"));
const Incentivos = lazy(() => import("@/pages/Incentivos"));
const TodosOsVistos = lazy(() => import("@/pages/TodosOsVistos"));
const AllVisas = lazy(() => import("@/pages/AllVisas"));
const GoldenVisaEN = lazy(() => import("@/pages/GoldenVisaEN"));
const GoldenVisaPT = lazy(() => import("@/pages/GoldenVisaPT"));
const StartupVisa = lazy(() => import("@/pages/StartupVisa"));
const StartupVisaEN = lazy(() => import("@/pages/StartupVisaEN"));
const GoldenVisaEligibility = lazy(() => import("@/pages/GoldenVisaEligibility"));
const VistoD1 = lazy(() => import("@/pages/VistoD1"));
const VisaD1 = lazy(() => import("@/pages/VisaD1"));
const VistoD2 = lazy(() => import("@/pages/VistoD2"));
const VisaD2 = lazy(() => import("@/pages/VisaD2"));
const VistoD3 = lazy(() => import("@/pages/VistoD3"));
const VisaD3 = lazy(() => import("@/pages/VisaD3"));
const VistoD4 = lazy(() => import("@/pages/VistoD4"));
const VisaD4 = lazy(() => import("@/pages/VisaD4"));
const VistoD5 = lazy(() => import("@/pages/VistoD5"));
const VisaD5 = lazy(() => import("@/pages/VisaD5"));
const VistoD6 = lazy(() => import("@/pages/VistoD6"));
const VisaD6 = lazy(() => import("@/pages/VisaD6"));
const VistoD7 = lazy(() => import("@/pages/VistoD7"));
const VisaD7 = lazy(() => import("@/pages/VisaD7"));
const VistoD8 = lazy(() => import("@/pages/VistoD8"));
const Documentacao = lazy(() => import("@/pages/guides/Documentacao"));
const Financas = lazy(() => import("@/pages/guides/Financas"));
const Habitacao = lazy(() => import("@/pages/guides/Habitacao"));
const Investimento = lazy(() => import("@/pages/guides/Investimento"));
const PublicVsPrivate = lazy(() => import("@/pages/healthcare/PublicVsPrivate"));
const HealthInsurance = lazy(() => import("@/pages/healthcare/HealthInsurance"));
const PrivateHospitals = lazy(() => import("@/pages/healthcare/PrivateHospitals"));
const VisaHealthcare = lazy(() => import("@/pages/healthcare/VisaHealthcare"));
const DentalCare = lazy(() => import("@/pages/healthcare/DentalCare"));
const ElderlyCare = lazy(() => import("@/pages/healthcare/ElderlyCare"));
const EmergencyServices = lazy(() => import("@/pages/healthcare/EmergencyServices"));
const SnsRegistration = lazy(() => import("@/pages/healthcare/SnsRegistration"));
const RetirementGuide = lazy(() => import("@/pages/guides/RetirementGuide"));
const InvestmentGuide = lazy(() => import("@/pages/guides/InvestmentGuide"));
const NomadGuide = lazy(() => import("@/pages/guides/NomadGuide"));
const BusinessGuide = lazy(() => import("@/pages/guides/BusinessGuide"));

function wrap(Lazy: LazyExoticComponent<ComponentType<object>>, path: string) {
  return <Route key={path} path={path} element={<Suspense fallback={<LoadingScreen />}><Lazy /></Suspense>} />;
}

/** Canonical /destinations/* routes (Fase 3.1–3.4) — must be invoked as fn inside <Routes>, not as <Component />. */
export function DestinationCanonicalRoutes() {
  const p = DESTINATION_ROUTES.portugal;

  return (
    <>
      {wrap(PortugalHub, p.hub.pt)}
      {wrap(SpainHub, DESTINATION_ROUTES.spain.hub.pt)}
      {wrap(ItalyHub, DESTINATION_ROUTES.italy.hub.pt)}
      {wrap(GreeceHub, DESTINATION_ROUTES.greece.hub.pt)}

      {wrap(TodosOsVistos, p.visas.all.pt)}
      {wrap(AllVisas, p.visas.all.en)}
      {wrap(GoldenVisaPT, p.visas.golden.pt)}
      {wrap(GoldenVisaEN, p.visas.golden.en)}
      {wrap(StartupVisa, p.visas.startup.pt)}
      {wrap(StartupVisaEN, p.visas.startup.en)}
      {wrap(GoldenVisaEligibility, p.visas.eligibility!.pt)}

      {wrap(VistoD1, p.visas.d1.pt)}
      {wrap(VisaD1, p.visas.d1.en)}
      {wrap(VistoD2, p.visas.d2.pt)}
      {wrap(VisaD2, p.visas.d2.en)}
      {wrap(VistoD3, p.visas.d3.pt)}
      {wrap(VisaD3, p.visas.d3.en)}
      {wrap(VistoD4, p.visas.d4.pt)}
      {wrap(VisaD4, p.visas.d4.en)}
      {wrap(VistoD5, p.visas.d5.pt)}
      {wrap(VisaD5, p.visas.d5.en)}
      {wrap(VistoD6, p.visas.d6.pt)}
      {wrap(VisaD6, p.visas.d6.en)}
      {wrap(VistoD7, p.visas.d7.pt)}
      {wrap(VisaD7, p.visas.d7.en)}
      {wrap(VistoD8, p.visas.d8.pt)}
      {wrap(VistoD8, p.visas.d8.en)}

      {wrap(Imobiliario, p.realEstate.pt)}
      {wrap(Imobiliario, p.realEstate.en)}
      {wrap(ComprarCasa, p.buyProperty.pt)}
      {wrap(ComprarCasa, p.buyProperty.en)}
      {wrap(Financiamento, p.financing.pt)}
      {wrap(Financiamento, p.financing.en)}
      {wrap(Negocios, p.business.pt)}
      {wrap(Negocios, p.business.en)}
      {wrap(ConsultoriaLegal, p.legal.pt)}
      {wrap(ConsultoriaLegal, p.legal.en)}
      {wrap(Saude, p.healthcare.pt)}
      {wrap(Saude, p.healthcare.en)}
      {wrap(GuiasEmigrantes, p.guides.pt)}
      {wrap(GuiasEmigrantes, p.guides.en)}
      {wrap(Incentivos, p.incentives.pt)}
      {wrap(Incentivos, p.incentives.en)}

      {wrap(Documentacao, `${p.guides.pt}/documentacao`)}
      {wrap(Financas, `${p.guides.pt}/financas`)}
      {wrap(Habitacao, `${p.guides.pt}/habitacao`)}
      {wrap(Investimento, `${p.guides.pt}/investimento`)}
      {wrap(Documentacao, `${p.guides.en}/documentacao`)}
      {wrap(Financas, `${p.guides.en}/financas`)}
      {wrap(Habitacao, `${p.guides.en}/habitacao`)}
      {wrap(Investimento, `${p.guides.en}/investimento`)}

      {wrap(PublicVsPrivate, `${p.healthcare.pt}/publico-vs-privado`)}
      {wrap(HealthInsurance, `${p.healthcare.pt}/seguros-saude`)}
      {wrap(PrivateHospitals, `${p.healthcare.pt}/hospitais-privados`)}
      {wrap(VisaHealthcare, `${p.healthcare.pt}/vistos-saude`)}
      {wrap(DentalCare, `${p.healthcare.pt}/cuidados-dentarios`)}
      {wrap(ElderlyCare, `${p.healthcare.pt}/cuidados-idosos`)}
      {wrap(EmergencyServices, `${p.healthcare.pt}/servicos-emergencia`)}
      {wrap(SnsRegistration, `${p.healthcare.pt}/inscricao-sns`)}
      {wrap(PublicVsPrivate, `${p.healthcare.en}/public-vs-private`)}
      {wrap(HealthInsurance, `${p.healthcare.en}/health-insurance`)}
      {wrap(PrivateHospitals, `${p.healthcare.en}/private-hospitals`)}
      {wrap(VisaHealthcare, `${p.healthcare.en}/visa-healthcare`)}
      {wrap(DentalCare, `${p.healthcare.en}/dental-care`)}
      {wrap(ElderlyCare, `${p.healthcare.en}/elderly-care`)}
      {wrap(EmergencyServices, `${p.healthcare.en}/emergency-services`)}
      {wrap(SnsRegistration, `${p.healthcare.en}/sns-registration`)}

      {wrap(RetirementGuide, p.retirementGuide.pt)}
      {wrap(RetirementGuide, p.retirementGuide.en)}
      {wrap(InvestmentGuide, p.investmentGuide.pt)}
      {wrap(InvestmentGuide, p.investmentGuide.en)}
      {wrap(NomadGuide, p.nomadGuide.pt)}
      {wrap(NomadGuide, p.nomadGuide.en)}
      {wrap(BusinessGuide, p.businessGuide.pt)}
      {wrap(BusinessGuide, p.businessGuide.en)}
    </>
  );
}
